import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { fetchVocabularies } from '../api/endpoints';
import { useResource } from '../hooks/useResource';
import type { Resource } from '../hooks/useResource';
import type { ImpactDef, PriorityDef, StatusDef, TypeDef, Vocabularies } from '../api/types';

/**
 * Statuses, types, priorities and impacts, loaded once.
 *
 * They are CONFIGURABLE data (db/004) — a status can be added or recoloured without a deploy — so
 * the app must never carry its own copy. It carries a lookup instead, and every lookup reports
 * whether it actually resolved:
 *
 * ⭐ `resolved: false` means "we are showing the raw code because we could not load the vocabulary",
 * and the header says so. ⛔ Silently title-casing `dev_to_do` into "Dev To Do" would produce a label
 * that is right often enough to hide the fact that the vocabulary never loaded.
 */
export interface Labelled {
  code: string;
  label: string;
  colour: string | null;
  resolved: boolean;
}

interface VocabularyContextValue {
  resource: Resource<Vocabularies>;
  statuses: StatusDef[];
  types: TypeDef[];
  priorities: PriorityDef[];
  impacts: ImpactDef[];
  status: (code: string | null | undefined) => Labelled | null;
  type: (code: string | null | undefined) => Labelled | null;
  priority: (code: string | null | undefined) => Labelled | null;
  impact: (code: string | null | undefined) => Labelled | null;
}

const VocabularyContext = createContext<VocabularyContextValue | null>(null);

const EMPTY: StatusDef[] = [];

function lookup<T extends { code: string; label: string }>(
  index: Map<string, T>,
  code: string | null | undefined,
  colourOf: (item: T) => string | null,
): Labelled | null {
  if (!code) return null;
  const found = index.get(code);
  if (!found) return { code, label: code, colour: null, resolved: false };
  return { code, label: found.label, colour: colourOf(found), resolved: true };
}

export function VocabularyProvider({ children }: { children: ReactNode }) {
  const resource = useResource<Vocabularies>((signal) => fetchVocabularies(signal), []);

  const value = useMemo<VocabularyContextValue>(() => {
    const data = resource.data;
    const statuses = data?.statuses ?? (EMPTY as StatusDef[]);
    const types = data?.types ?? [];
    const priorities = data?.priorities ?? [];
    const impacts = data?.impacts ?? [];

    const statusIndex = new Map(statuses.map((item) => [item.code, item]));
    const typeIndex = new Map(types.map((item) => [item.code, item]));
    const priorityIndex = new Map(priorities.map((item) => [item.code, item]));
    const impactIndex = new Map(impacts.map((item) => [item.code, item]));

    return {
      resource,
      statuses,
      types,
      priorities,
      impacts,
      status: (code) => lookup(statusIndex, code, (item) => item.colour),
      type: (code) => lookup(typeIndex, code, () => null),
      priority: (code) => lookup(priorityIndex, code, (item) => item.colour),
      impact: (code) => lookup(impactIndex, code, () => null),
    };
  }, [resource]);

  return <VocabularyContext.Provider value={value}>{children}</VocabularyContext.Provider>;
}

export function useVocabulary(): VocabularyContextValue {
  const context = useContext(VocabularyContext);
  if (!context) throw new Error('useVocabulary must be used inside VocabularyProvider');
  return context;
}
